import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { checkoutLicense } from "features/orders";
import type { Entitlement, LicenseTier } from "features/orders";
import { parseApiError } from "shared/api/client/errorParser";
import styles from "./LicenseCheckoutModal.module.css";

export type LicenseOption = {
  tier: LicenseTier;
  label: string;
  description: string;
  priceLabel: string;
};

export function buildLicenseOptions(priceLabel: string): LicenseOption[] {
  const personalPrice = /^free$/i.test(priceLabel.trim()) || !priceLabel.trim() ? "€1.99" : priceLabel;
  return [
    {
      tier: "Personal",
      label: "Personal XR Stream & Download",
      description: "Stream in the browser or download to your paired headset. Yours to keep.",
      priceLabel: personalPrice,
    },
    {
      tier: "VenueScreening",
      label: "Venue Screening License (31-Day Exhibition)",
      description: "Exhibit on registered venue headsets for 31 days, with offline licenses.",
      priceLabel: "€29.00",
    },
  ];
}

/** Offline/unauthenticated fallback so the demo flow still completes locally. */
export function localFallbackEntitlement(
  title: string,
  tier: LicenseTier,
  priceLabel: string
): Entitlement {
  const now = new Date();
  const expiresAt =
    tier === "VenueScreening"
      ? new Date(now.getTime() + 31 * 86400000).toISOString()
      : null;
  const transactionId = `local_${now.getTime().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  return {
    id: `purchase-${transactionId}`,
    title,
    date: now.toISOString(),
    status: "active",
    type: tier === "VenueScreening" ? "Venue screening license (31-day)" : "Personal XR stream & download",
    price: priceLabel,
    transactionId,
    expiresAt,
  };
}

export type LicenseCheckoutModalProps = {
  open: boolean;
  title: string;
  assetUploadId?: number;
  /** Catalog price label (e.g. "Free", "EUR 3.99") used for the personal tier. */
  priceLabel: string;
  onClose: () => void;
  onLicensed: (entitlement: Entitlement) => void;
};

export function LicenseCheckoutModal({
  open,
  title,
  assetUploadId,
  priceLabel,
  onClose,
  onLicensed,
}: LicenseCheckoutModalProps) {
  const options = buildLicenseOptions(priceLabel);
  const [tier, setTier] = useState<LicenseTier>("Personal");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string>("");
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    setTier("Personal");
    setNotice("");
    setSubmitting(false);
    confirmRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const selected = options.find((option) => option.tier === tier) ?? options[0];

  const complete = async () => {
    setSubmitting(true);
    setNotice("");
    try {
      if (!assetUploadId) throw new Error("This title is not available for checkout yet.");
      const entitlement = await checkoutLicense({ assetUploadId, licenseTier: tier });
      onLicensed(entitlement);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      // Client errors (401/400/409/404) are real decisions; anything else degrades to a local license.
      if (status && status >= 400 && status < 500) {
        setNotice(parseApiError(error, "Checkout failed. Please try again."));
        setSubmitting(false);
        return;
      }
      onLicensed(localFallbackEntitlement(title, tier, selected.priceLabel));
    }
  };

  const dialog = (
    <div
      className={styles.backdrop}
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
      data-testid="license-modal-backdrop"
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="License this title"
      >
        <header className={styles.header}>
          <p className={styles.eyebrow}>Licensing &amp; checkout</p>
          <h2 className={styles.heading}>{title}</h2>
          <p className={styles.subheading}>
            Choose how you would like to license this title. Purchases appear in your order history.
          </p>
        </header>

        <fieldset className={styles.options} disabled={submitting}>
          <legend className={styles.srOnly}>License tier</legend>
          {options.map((option) => {
            const checked = option.tier === tier;
            return (
              <label
                key={option.tier}
                className={[styles.option, checked ? styles.optionSelected : ""].filter(Boolean).join(" ")}
              >
                <input
                  type="radio"
                  name="license-tier"
                  value={option.tier}
                  checked={checked}
                  onChange={() => setTier(option.tier)}
                  className={styles.radio}
                />
                <span className={styles.optionBody}>
                  <span className={styles.optionLabel}>{option.label}</span>
                  <span className={styles.optionDescription}>{option.description}</span>
                </span>
                <span className={styles.optionPrice}>{option.priceLabel}</span>
              </label>
            );
          })}
        </fieldset>

        {notice ? (
          <p role="alert" className={styles.notice}>
            {notice}
          </p>
        ) : null}

        <footer className={styles.footer}>
          <p className={styles.summary} data-testid="license-summary">
            <span>{selected.label}</span>
            <strong>{selected.priceLabel}</strong>
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancel}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              ref={confirmRef}
              type="button"
              className={styles.confirm}
              onClick={() => void complete()}
              disabled={submitting}
            >
              {submitting ? "Processing…" : "Complete Purchase"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}

export default LicenseCheckoutModal;
